import { serialize, type CookieSerializeOptions } from 'cookie'
import type { IncomingMessage } from 'node:http'
import type { NextApiResponse } from 'next'

export const SEARCH_FORM_UI_VARIANT_COOKIE = 'hp_sfv'
export const DIRECT_FLIGHT_LABEL_UI_VARIANT_COOKIE = 'hp_dfl'

/** Cookie değerinde A/B harfi kullanılmaz; 1 = B, 0 = A */
export type HomepageUiVariants = {
  searchFormVariant: string
  directFlightLabelVariant: 'A' | 'B'
}

const uiVariantsByRequest = new WeakMap<IncomingMessage, HomepageUiVariants>()

export function encodeUiVariantFlag(variant: string | undefined): '0' | '1' {
  return variant?.toUpperCase() === 'B' ? '1' : '0'
}

export function decodeUiVariantFlag(raw: string | undefined | null): 'A' | 'B' {
  return raw === '1' ? 'B' : 'A'
}

export function attachHomepageUiVariantsToRequest(req: IncomingMessage, variants: HomepageUiVariants): void {
  uiVariantsByRequest.set(req, variants)
}

export function getHomepageUiVariantsFromRequest(req: IncomingMessage | undefined | null): HomepageUiVariants | null {
  if (!req) return null
  return uiVariantsByRequest.get(req) ?? null
}

function readCookieValue(cookieHeader: string, name: string): string | null {
  const prefix = `${name}=`
  const match = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
  return match ? match.slice(prefix.length) : null
}

export function getHomepageUiVariantsFromCookieHeader(cookieHeader = ''): HomepageUiVariants {
  const source = cookieHeader ?? ''
  return {
    searchFormVariant: decodeUiVariantFlag(readCookieValue(source, SEARCH_FORM_UI_VARIANT_COOKIE)),
    directFlightLabelVariant: decodeUiVariantFlag(readCookieValue(source, DIRECT_FLIGHT_LABEL_UI_VARIANT_COOKIE)),
  }
}

export function getHomepageUiVariantsFromDocument(readCookie: () => string | null = readDocumentCookie): HomepageUiVariants {
  const cookie = readCookie()
  if (cookie == null) {
    return { searchFormVariant: 'A', directFlightLabelVariant: 'A' }
  }
  return getHomepageUiVariantsFromCookieHeader(cookie)
}

/** @internal test/SSR helper — document yoksa null */
export function readDocumentCookie(): string | null {
  const doc = (globalThis as { document?: { cookie?: string } }).document
  if (doc == null || typeof doc.cookie !== 'string') return null
  return doc.cookie
}

const DEFAULT_UI_VARIANT_COOKIE_OPTIONS: CookieSerializeOptions = {
  path: '/',
  sameSite: 'lax',
  maxAge: 60 * 60 * 24 * 7,
}

/**
 * UI variant cookie'lerini response'a yazar.
 * Değerler 0/1'dir; __NEXT_DATA__'ya yazılmaz.
 */
export function setHomepageUiVariantCookies(
  res: NextApiResponse | { setHeader: (name: string, value: string | string[]) => void },
  variants: HomepageUiVariants,
  options: CookieSerializeOptions = {}
): void {
  const cookieOptions = { ...DEFAULT_UI_VARIANT_COOKIE_OPTIONS, ...options }
  res.setHeader('Set-Cookie', [
    serialize(SEARCH_FORM_UI_VARIANT_COOKIE, encodeUiVariantFlag(variants.searchFormVariant), cookieOptions),
    serialize(DIRECT_FLIGHT_LABEL_UI_VARIANT_COOKIE, encodeUiVariantFlag(variants.directFlightLabelVariant), cookieOptions),
  ])
}
